import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  Button,
  Spinner,
  Alert,
  Form,
  Tab,
  Tabs,
  ProgressBar,
  Modal,
} from 'react-bootstrap';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ReferenceLine,
} from 'recharts';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const COLORS = ['#006e39', '#0077b6', '#e76f51', '#2a9d8f', '#f4a261', '#9c27b0', '#ff9800', '#607d8b'];
const MONTH_NAMES = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

const SdaEventPlanning = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Data states
  const [overview, setOverview] = useState(null);
  const [geographic, setGeographic] = useState(null);
  const [temporal, setTemporal] = useState(null);
  const [demographic, setDemographic] = useState(null);
  const [growthTrends, setGrowthTrends] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [topAssociations, setTopAssociations] = useState(null);
  const [yearComparison, setYearComparison] = useState(null);

  // AI states
  const [aiEnabled, setAiEnabled] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const [aiExplanation, setAiExplanation] = useState(null);

  // Filters
  const [selectedDiscipline, setSelectedDiscipline] = useState(() => user?.discipline || '');
  const [availableDisciplines, setAvailableDisciplines] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [regions, setRegions] = useState([]);

  // Load available disciplines for admin users
  useEffect(() => {
    if (user?.role === 'admin') {
      api.get('/sda/dashboard/sports')
        .then(res => setAvailableDisciplines(res.data?.data?.sports || []))
        .catch(() => setAvailableDisciplines([]));
    }
  }, [user?.role]);

  // Check AI availability
  const checkAiStatus = useCallback(async () => {
    try {
      const response = await api.get('/sda/event-planning/ai/status');
      setAiEnabled(response.data.data?.aiEnabled || false);
    } catch {
      setAiEnabled(false);
    }
  }, []);

  // Effective discipline: from selection (admin) or from user profile (SDA roles)
  const effectiveDiscipline = selectedDiscipline || user?.discipline || '';

  // Fetch all data
  const fetchData = useCallback(async () => {
    // Admin must select a discipline first
    if (user?.role === 'admin' && !selectedDiscipline) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const qp = new URLSearchParams();
      if (effectiveDiscipline) qp.set('discipline', effectiveDiscipline);
      if (selectedRegion) qp.set('region', selectedRegion);
      const params = qp.toString() ? `?${qp.toString()}` : '';

      const qpNoRegion = new URLSearchParams();
      if (effectiveDiscipline) qpNoRegion.set('discipline', effectiveDiscipline);
      const paramsNoRegion = qpNoRegion.toString() ? `?${qpNoRegion.toString()}` : '';

      const [overviewRes, geographicRes, temporalRes, demographicRes, growthRes, suggestionsRes, associationsRes, yearRes] =
        await Promise.all([
          api.get(`/sda/event-planning/overview${params}`),
          api.get(`/sda/event-planning/geographic${params}`),
          api.get(`/sda/event-planning/temporal${paramsNoRegion}`),
          api.get(`/sda/event-planning/demographic${params}`),
          api.get(`/sda/event-planning/growth-trends${paramsNoRegion}`),
          api.get(`/sda/event-planning/suggestions${params}`),
          api.get(`/sda/event-planning/top-associations${params}`),
          api.get(`/sda/event-planning/year-comparison${params}`),
        ]);

      setOverview(overviewRes.data.data);
      setGeographic(geographicRes.data.data);
      setTemporal(temporalRes.data.data);
      setDemographic(demographicRes.data.data);
      setGrowthTrends(growthRes.data.data);
      setSuggestions(suggestionsRes.data.data);
      setTopAssociations(associationsRes.data.data);
      setYearComparison(yearRes.data.data);

      // Extract unique regions for filter
      if (geographicRes.data.data?.regionalSummary) {
        setRegions(geographicRes.data.data.regionalSummary.map(r => r.region));
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching event planning data:', err);
      setError(err.response?.data?.message || 'Errore nel caricamento dei dati');
      setLoading(false);
    }
  }, [selectedDiscipline, selectedRegion, effectiveDiscipline, user?.role]);

  // Fetch AI analysis
  const fetchAiAnalysis = async () => {
    if (!aiEnabled) return;

    try {
      setAiLoading(true);
      const response = await api.post('/sda/event-planning/ai/analyze', {
        discipline: effectiveDiscipline || undefined,
        region: selectedRegion || undefined,
      });
      setAiAnalysis(response.data.data?.aiAnalysis || null);
      setAiLoading(false);
    } catch (err) {
      console.error('Error fetching AI analysis:', err);
      setAiLoading(false);
    }
  };

  // Fetch AI suggestions
  const fetchAiSuggestions = async () => {
    if (!aiEnabled) return;

    try {
      setAiLoading(true);
      const response = await api.post('/sda/event-planning/ai/suggestions', {
        discipline: effectiveDiscipline || undefined,
        region: selectedRegion || undefined,
      });
      setAiSuggestions(response.data.data?.aiSuggestions || null);
      setAiLoading(false);
      setShowAiModal(true);
    } catch (err) {
      console.error('Error fetching AI suggestions:', err);
      setAiLoading(false);
    }
  };

  // Get AI explanation for data
  const getAiExplanation = async (data, context) => {
    if (!aiEnabled) return;

    try {
      setAiLoading(true);
      const response = await api.post('/sda/event-planning/ai/explain', { data, context });
      setAiExplanation(response.data.data?.explanation || 'Nessuna spiegazione disponibile');
      setAiLoading(false);
    } catch (err) {
      console.error('Error getting AI explanation:', err);
      setAiExplanation('Errore nel recupero della spiegazione AI');
      setAiLoading(false);
    }
  };

  useEffect(() => {
    checkAiStatus();
    fetchData();
  }, [checkAiStatus, fetchData]);

  // Render loading state
  if (loading && !overview) {
    // Admin hasn't chosen a discipline yet — show selector instead of spinner
    if (user?.role === 'admin' && !selectedDiscipline) {
      return (
        <Container className="mt-4">
          <div
            className="glass-container p-5 rounded-4 text-center"
            style={{ backdropFilter: 'blur(10px)', backgroundColor: 'rgba(255, 255, 255, 0.95)' }}
          >
            <i className="bi bi-funnel fs-1 mb-3" style={{ color: '#006e39' }}></i>
            <h3 style={{ color: '#006e39' }}>Seleziona una Disciplina</h3>
            <p className="text-muted">Scegli la disciplina SDA per visualizzare la pianificazione eventi.</p>
            {availableDisciplines.length > 0 ? (
              <Form.Select
                style={{ maxWidth: '350px', margin: '0 auto' }}
                value={selectedDiscipline}
                onChange={e => setSelectedDiscipline(e.target.value)}
              >
                <option value="">-- Seleziona disciplina --</option>
                {availableDisciplines.map(d => (
                  <option key={d.sport} value={d.sport}>
                    {d.sport} ({d.memberCount?.toLocaleString('it-IT') || 0} tesserati)
                  </option>
                ))}
              </Form.Select>
            ) : (
              <Spinner animation="border" style={{ color: '#006e39' }} />
            )}
          </div>
        </Container>
      );
    }
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" style={{ color: '#006e39' }}>
          <span className="visually-hidden">Caricamento...</span>
        </Spinner>
        <p className="mt-3">Caricamento strumento pianificazione eventi...</p>
      </Container>
    );
  }

  // Render error state
  if (error && !overview) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <Alert.Heading>Errore</Alert.Heading>
          <p>{error}</p>
          <Button onClick={fetchData} variant="outline-danger">
            Riprova
          </Button>
        </Alert>
      </Container>
    );
  }

  // Format month
  const formatMonth = month => {
    if (!month) return '';
    const [year, m] = month.split('-');
    return `${MONTH_NAMES[parseInt(m) - 1]} ${year.slice(2)}`;
  };

  // Get priority badge variant
  const getPriorityVariant = priority => {
    switch (priority) {
      case 'high':
        return 'danger';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'secondary';
    }
  };

  return (
    <div className="container-fluid min-vh-100" style={{ background: 'linear-gradient(135deg, #006e39, #52b788)' }}>
      <div className="row p-4">
        <div className="col-12">
          <div
            className="glass-container p-4 rounded-4"
            style={{ backdropFilter: 'blur(10px)', backgroundColor: 'rgba(255, 255, 255, 0.95)' }}
          >
            {/* Header */}
            <Row className="mb-4 align-items-center">
              <Col>
                <h1 style={{ color: '#006e39' }} className="mb-2">
                  <i className="bi bi-calendar-event me-3"></i>
                  Pianificazione Eventi SDA
                </h1>
                <p className="text-muted mb-0">
                  <i className="bi bi-trophy me-2"></i>
                  Disciplina: <strong>{effectiveDiscipline || overview?.discipline || 'Non specificata'}</strong>
                  {aiEnabled && (
                    <Badge bg="success" className="ms-2">
                      <i className="bi bi-stars me-1"></i>AI Disponibile
                    </Badge>
                  )}
                </p>
              </Col>
              <Col xs="auto" className="d-flex gap-2">
                {user?.role === 'admin' && availableDisciplines.length > 0 && (
                  <Form.Select
                    size="sm"
                    style={{ width: '220px' }}
                    value={selectedDiscipline}
                    onChange={e => {
                      setSelectedDiscipline(e.target.value);
                      setSelectedRegion('');
                    }}
                  >
                    <option value="">-- Disciplina --</option>
                    {availableDisciplines.map(d => (
                      <option key={d.sport} value={d.sport}>
                        {d.sport}
                      </option>
                    ))}
                  </Form.Select>
                )}
                <Form.Select
                  size="sm"
                  style={{ width: '200px' }}
                  value={selectedRegion}
                  onChange={e => setSelectedRegion(e.target.value)}
                >
                  <option value="">Tutte le regioni</option>
                  {regions.map(r => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </Form.Select>
                {aiEnabled && (
                  <Button variant="outline-primary" onClick={fetchAiSuggestions} disabled={aiLoading}>
                    {aiLoading ? (
                      <Spinner size="sm" animation="border" />
                    ) : (
                      <>
                        <i className="bi bi-stars me-2"></i>Suggerimenti AI
                      </>
                    )}
                  </Button>
                )}
                <Button variant="outline-success" onClick={fetchData} disabled={loading}>
                  {loading ? <Spinner size="sm" animation="border" /> : <i className="bi bi-arrow-clockwise"></i>}
                </Button>
              </Col>
            </Row>

            {/* Summary Cards */}
            {overview && (
              <Row className="mb-4">
                <Col md={3}>
                  <Card className="h-100 border-0 shadow-sm" style={{ borderLeft: '4px solid #006e39' }}>
                    <Card.Body className="d-flex align-items-center">
                      <div className="rounded-circle p-3 me-3" style={{ backgroundColor: '#006e3915' }}>
                        <i className="bi bi-people-fill fs-2" style={{ color: '#006e39' }}></i>
                      </div>
                      <div>
                        <h6 className="text-muted mb-1">Tesserati Totali</h6>
                        <h3 className="mb-0" style={{ color: '#006e39' }}>
                          {overview.summary?.totalMembers?.toLocaleString('it-IT') || 0}
                        </h3>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="h-100 border-0 shadow-sm" style={{ borderLeft: '4px solid #0077b6' }}>
                    <Card.Body className="d-flex align-items-center">
                      <div className="rounded-circle p-3 me-3" style={{ backgroundColor: '#0077b615' }}>
                        <i className="bi bi-building fs-2" style={{ color: '#0077b6' }}></i>
                      </div>
                      <div>
                        <h6 className="text-muted mb-1">Associazioni</h6>
                        <h3 className="mb-0" style={{ color: '#0077b6' }}>
                          {overview.summary?.totalAssociations?.toLocaleString('it-IT') || 0}
                        </h3>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="h-100 border-0 shadow-sm" style={{ borderLeft: '4px solid #e76f51' }}>
                    <Card.Body className="d-flex align-items-center">
                      <div className="rounded-circle p-3 me-3" style={{ backgroundColor: '#e76f5115' }}>
                        <i className="bi bi-geo-alt-fill fs-2" style={{ color: '#e76f51' }}></i>
                      </div>
                      <div>
                        <h6 className="text-muted mb-1">Regioni Attive</h6>
                        <h3 className="mb-0" style={{ color: '#e76f51' }}>
                          {overview.summary?.totalRegions || 0}
                        </h3>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="h-100 border-0 shadow-sm" style={{ borderLeft: '4px solid #2a9d8f' }}>
                    <Card.Body className="d-flex align-items-center">
                      <div className="rounded-circle p-3 me-3" style={{ backgroundColor: '#2a9d8f15' }}>
                        <i className="bi bi-lightbulb-fill fs-2" style={{ color: '#2a9d8f' }}></i>
                      </div>
                      <div>
                        <h6 className="text-muted mb-1">Suggerimenti</h6>
                        <h3 className="mb-0" style={{ color: '#2a9d8f' }}>
                          {suggestions?.suggestions?.length || 0}
                        </h3>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Main Tabs */}
            <Tabs activeKey={activeTab} onSelect={k => setActiveTab(k)} className="mb-4">
              {/* Overview Tab */}
              <Tab eventKey="overview" title={<><i className="bi bi-grid-3x3-gap me-2"></i>Panoramica</>}>
                {/* Suggestions Section */}
                {suggestions && suggestions.suggestions && suggestions.suggestions.length > 0 && (
                  <Card className="mb-4">
                    <Card.Header className="bg-success text-white">
                      <i className="bi bi-lightbulb me-2"></i>
                      <strong>Suggerimenti Basati sui Dati</strong>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        {suggestions.suggestions.map((suggestion, idx) => (
                          <Col md={6} lg={3} key={idx} className="mb-3">
                            <Card className="h-100 border-0 shadow-sm">
                              <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                                <strong>{suggestion.title}</strong>
                                <Badge bg={getPriorityVariant(suggestion.priority)}>{suggestion.priority}</Badge>
                              </Card.Header>
                              <Card.Body>
                                <p className="small mb-2">{suggestion.description}</p>
                                <small className="text-muted">
                                  <i className="bi bi-info-circle me-1"></i>
                                  {suggestion.reasoning}
                                </small>
                              </Card.Body>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    </Card.Body>
                  </Card>
                )}

                {/* Best Locations & Months */}
                <Row className="mb-4">
                  <Col lg={6}>
                    <Card className="h-100">
                      <Card.Header className="bg-white">
                        <strong>
                          <i className="bi bi-geo-alt me-2"></i>Migliori Località per Eventi
                        </strong>
                      </Card.Header>
                      <Card.Body>
                        {overview?.recommendations?.bestLocations?.length > 0 ? (
                          <Table striped hover size="sm">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Località</th>
                                <th>Regione</th>
                                <th>Tesserati</th>
                              </tr>
                            </thead>
                            <tbody>
                              {overview.recommendations.bestLocations.slice(0, 5).map((loc, idx) => (
                                <tr key={idx}>
                                  <td>
                                    {idx < 3 ? (
                                      <Badge bg={idx === 0 ? 'warning' : idx === 1 ? 'secondary' : 'danger'}>
                                        {idx + 1}
                                      </Badge>
                                    ) : (
                                      idx + 1
                                    )}
                                  </td>
                                  <td>{loc.city || loc.territory || '-'}</td>
                                  <td>{loc.region}</td>
                                  <td>
                                    <Badge bg="success">{loc.memberCount}</Badge>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        ) : (
                          <Alert variant="info">Nessun dato disponibile</Alert>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col lg={6}>
                    <Card className="h-100">
                      <Card.Header className="bg-white">
                        <strong>
                          <i className="bi bi-calendar3 me-2"></i>Migliori Periodi per Eventi
                        </strong>
                      </Card.Header>
                      <Card.Body>
                        {overview?.recommendations?.bestMonths?.length > 0 ? (
                          <>
                            <ResponsiveContainer width="100%" height={200}>
                              <BarChart data={overview.recommendations.bestMonths}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="monthName" />
                                <YAxis />
                                <Tooltip />
                                <Bar dataKey="avgRegistrations" fill="#006e39" name="Media Iscrizioni" />
                              </BarChart>
                            </ResponsiveContainer>
                            <small className="text-muted">
                              <i className="bi bi-info-circle me-1"></i>I mesi con più iscrizioni indicano periodi di
                              maggiore interesse
                            </small>
                          </>
                        ) : (
                          <Alert variant="info">Nessun dato disponibile</Alert>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Tab>

              {/* Geographic Tab */}
              <Tab eventKey="geographic" title={<><i className="bi bi-map me-2"></i>Distribuzione Geografica</>}>
                <Row className="mb-4">
                  <Col lg={12}>
                    <Card>
                      <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                        <strong>
                          <i className="bi bi-bar-chart me-2"></i>Tesserati per Regione
                        </strong>
                        <Badge bg="success">{geographic?.regionalSummary?.length || 0} regioni</Badge>
                      </Card.Header>
                      <Card.Body>
                        <ResponsiveContainer width="100%" height={400}>
                          <BarChart data={geographic?.regionalSummary || []} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" />
                            <YAxis dataKey="region" type="category" width={150} tick={{ fontSize: 12 }} />
                            <Tooltip formatter={value => value.toLocaleString('it-IT')} />
                            <Legend />
                            <Bar dataKey="memberCount" fill="#006e39" name="Tesserati" />
                            <Bar dataKey="associationCount" fill="#0077b6" name="Associazioni" />
                          </BarChart>
                        </ResponsiveContainer>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <Row>
                  <Col lg={12}>
                    <Card>
                      <Card.Header className="bg-white">
                        <strong>
                          <i className="bi bi-pin-map me-2"></i>Dettaglio per Località
                        </strong>
                      </Card.Header>
                      <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <Table striped hover size="sm">
                          <thead style={{ position: 'sticky', top: 0, background: 'white' }}>
                            <tr>
                              <th>Città</th>
                              <th>Territorio</th>
                              <th>Regione</th>
                              <th>Tesserati</th>
                              <th>Associazioni</th>
                              <th>Età Media</th>
                            </tr>
                          </thead>
                          <tbody>
                            {geographic?.locations?.map((loc, idx) => (
                              <tr key={idx}>
                                <td>{loc.city || '-'}</td>
                                <td>{loc.territory}</td>
                                <td>{loc.region}</td>
                                <td>
                                  <Badge bg="success">{loc.memberCount}</Badge>
                                </td>
                                <td>{loc.associationCount}</td>
                                <td>{loc.avgAge || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Tab>

              {/* Temporal Tab */}
              <Tab eventKey="temporal" title={<><i className="bi bi-clock-history me-2"></i>Pattern Temporali</>}>
                <Row className="mb-4">
                  <Col lg={8}>
                    <Card>
                      <Card.Header className="bg-white">
                        <strong>
                          <i className="bi bi-graph-up me-2"></i>Trend Iscrizioni (Ultimi 24 Mesi)
                        </strong>
                      </Card.Header>
                      <Card.Body>
                        <ResponsiveContainer width="100%" height={300}>
                          <AreaChart data={temporal?.monthlyTrends || []}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" tickFormatter={formatMonth} tick={{ fontSize: 11 }} />
                            <YAxis />
                            <Tooltip labelFormatter={formatMonth} formatter={value => value.toLocaleString('it-IT')} />
                            <Legend />
                            <Area
                              type="monotone"
                              dataKey="registrationCount"
                              stroke="#006e39"
                              fill="#006e3930"
                              name="Nuove Iscrizioni"
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col lg={4}>
                    <Card className="h-100">
                      <Card.Header className="bg-white">
                        <strong>
                          <i className="bi bi-calendar-week me-2"></i>Pattern Stagionale
                        </strong>
                      </Card.Header>
                      <Card.Body>
                        <ResponsiveContainer width="100%" height={300}>
                          <RadarChart data={temporal?.seasonalPatterns || []}>
                            <PolarGrid />
                            <PolarAngleAxis dataKey="monthName" tick={{ fontSize: 11 }} />
                            <PolarRadiusAxis />
                            <Radar
                              name="Iscrizioni"
                              dataKey="avgRegistrations"
                              stroke="#006e39"
                              fill="#006e39"
                              fillOpacity={0.6}
                            />
                          </RadarChart>
                        </ResponsiveContainer>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <Row>
                  <Col lg={12}>
                    <Card>
                      <Card.Header className="bg-white">
                        <strong>
                          <i className="bi bi-pie-chart me-2"></i>Distribuzione Trimestrale
                        </strong>
                      </Card.Header>
                      <Card.Body>
                        <Row className="text-center">
                          {temporal?.quarterlyPatterns?.map((q, idx) => (
                            <Col key={idx} md={3}>
                              <Card className="border-0 shadow-sm">
                                <Card.Body>
                                  <h5 className="text-muted">{q.quarterName}</h5>
                                  <h2 style={{ color: COLORS[idx % COLORS.length] }}>
                                    {q.registrationCount.toLocaleString('it-IT')}
                                  </h2>
                                  <small className="text-muted">{q.associationCount} associazioni attive</small>
                                </Card.Body>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Tab>

              {/* Demographic Tab */}
              <Tab eventKey="demographic" title={<><i className="bi bi-people me-2"></i>Demografia</>}>
                <Row className="mb-4">
                  <Col lg={4}>
                    <Card className="h-100">
                      <Card.Header className="bg-white">
                        <strong>
                          <i className="bi bi-gender-ambiguous me-2"></i>Distribuzione per Genere
                        </strong>
                      </Card.Header>
                      <Card.Body>
                        <ResponsiveContainer width="100%" height={250}>
                          <PieChart>
                            <Pie
                              data={demographic?.genderDistribution || []}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                              outerRadius={80}
                              dataKey="count"
                              nameKey="gender"
                            >
                              {demographic?.genderDistribution?.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={value => value.toLocaleString('it-IT')} />
                          </PieChart>
                        </ResponsiveContainer>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col lg={8}>
                    <Card className="h-100">
                      <Card.Header className="bg-white">
                        <strong>
                          <i className="bi bi-bar-chart me-2"></i>Distribuzione per Età
                        </strong>
                      </Card.Header>
                      <Card.Body>
                        <ResponsiveContainer width="100%" height={250}>
                          <BarChart data={demographic?.ageDistribution || []}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="ageGroup" />
                            <YAxis />
                            <Tooltip formatter={value => value.toLocaleString('it-IT')} />
                            <Legend />
                            <Bar dataKey="maleCount" stackId="a" fill="#0077b6" name="Maschi" />
                            <Bar dataKey="femaleCount" stackId="a" fill="#e76f51" name="Femmine" />
                          </BarChart>
                        </ResponsiveContainer>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <Row>
                  <Col lg={12}>
                    <Card>
                      <Card.Header className="bg-white">
                        <strong>
                          <i className="bi bi-table me-2"></i>Dettaglio Demografico per Fascia d'Età
                        </strong>
                      </Card.Header>
                      <Card.Body>
                        <Table striped hover>
                          <thead>
                            <tr>
                              <th>Fascia d'Età</th>
                              <th>Totale</th>
                              <th>Maschi</th>
                              <th>Femmine</th>
                              <th>Distribuzione</th>
                            </tr>
                          </thead>
                          <tbody>
                            {demographic?.ageDistribution?.map((age, idx) => {
                              const total = demographic.ageDistribution.reduce((sum, a) => sum + a.count, 0);
                              const percentage = total > 0 ? (age.count / total) * 100 : 0;
                              return (
                                <tr key={idx}>
                                  <td>
                                    <strong>{age.ageGroup}</strong>
                                  </td>
                                  <td>
                                    <Badge bg="success">{age.count.toLocaleString('it-IT')}</Badge>
                                  </td>
                                  <td>{age.maleCount.toLocaleString('it-IT')}</td>
                                  <td>{age.femaleCount.toLocaleString('it-IT')}</td>
                                  <td>
                                    <ProgressBar now={percentage} label={`${percentage.toFixed(1)}%`} variant="success" />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Tab>

              {/* Growth Tab */}
              <Tab eventKey="growth" title={<><i className="bi bi-graph-up-arrow me-2"></i>Crescita</>}>
                <Row className="mb-4">
                  <Col lg={8}>
                    <Card>
                      <Card.Header className="bg-white">
                        <strong>
                          <i className="bi bi-graph-up me-2"></i>Trend di Crescita
                        </strong>
                      </Card.Header>
                      <Card.Body>
                        <ResponsiveContainer width="100%" height={350}>
                          <LineChart data={growthTrends?.monthlyTrends || []}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" tickFormatter={formatMonth} tick={{ fontSize: 11 }} />
                            <YAxis yAxisId="left" />
                            <YAxis yAxisId="right" orientation="right" />
                            <Tooltip labelFormatter={formatMonth} formatter={value => value.toLocaleString('it-IT')} />
                            <Legend />
                            <Line
                              yAxisId="left"
                              type="monotone"
                              dataKey="newRegistrations"
                              stroke="#006e39"
                              strokeWidth={2}
                              name="Nuovi Tesserati"
                              dot={{ r: 4 }}
                            />
                            <Line
                              yAxisId="right"
                              type="monotone"
                              dataKey="cumulativeTotal"
                              stroke="#0077b6"
                              strokeWidth={2}
                              strokeDasharray="5 5"
                              name="Totale Cumulativo"
                              dot={{ r: 3 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col lg={4}>
                    <Card className="h-100">
                      <Card.Header className="bg-white">
                        <strong>
                          <i className="bi bi-speedometer2 me-2"></i>Riepilogo Crescita
                        </strong>
                      </Card.Header>
                      <Card.Body>
                        <div className="text-center mb-4">
                          <h2 style={{ color: '#006e39' }}>
                            {growthTrends?.summary?.totalNewRegistrations?.toLocaleString('it-IT') || 0}
                          </h2>
                          <p className="text-muted mb-0">Nuovi tesserati nel periodo</p>
                        </div>
                        <hr />
                        <div className="text-center">
                          <h4 style={{ color: '#0077b6' }}>{growthTrends?.summary?.averageMonthlyGrowth || '0'}%</h4>
                          <p className="text-muted mb-0">Crescita media mensile</p>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <Row>
                  <Col lg={12}>
                    <Card>
                      <Card.Header className="bg-white">
                        <strong>
                          <i className="bi bi-arrow-up-right me-2"></i>Crescita per Regione (Ultimi 3 Mesi)
                        </strong>
                      </Card.Header>
                      <Card.Body>
                        <Table striped hover>
                          <thead>
                            <tr>
                              <th>Regione</th>
                              <th>Ultimi 3 Mesi</th>
                              <th>3 Mesi Precedenti</th>
                              <th>Variazione</th>
                            </tr>
                          </thead>
                          <tbody>
                            {growthTrends?.regionalGrowth?.map((r, idx) => (
                              <tr key={idx}>
                                <td>
                                  <strong>{r.region}</strong>
                                </td>
                                <td>
                                  <Badge bg="success">{r.last3Months}</Badge>
                                </td>
                                <td>{r.prev3Months}</td>
                                <td>
                                  {r.growthRate !== 'N/A' ? (
                                    <Badge bg={parseFloat(r.growthRate) >= 0 ? 'success' : 'danger'}>
                                      {parseFloat(r.growthRate) >= 0 ? '+' : ''}
                                      {r.growthRate}%
                                    </Badge>
                                  ) : (
                                    <Badge bg="secondary">N/A</Badge>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Tab>

              {/* Associations Tab */}
              <Tab eventKey="associations" title={<><i className="bi bi-building me-2"></i>Associazioni</>}>
                <Card>
                  <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                    <strong>
                      <i className="bi bi-trophy me-2"></i>Top Associazioni per Tesserati
                    </strong>
                    <Badge bg="success">{topAssociations?.total || 0} associazioni</Badge>
                  </Card.Header>
                  <Card.Body>
                    <Table striped hover responsive>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Associazione</th>
                          <th>Città</th>
                          <th>Territorio</th>
                          <th>Tesserati</th>
                          <th>Nuovi (30gg)</th>
                          <th>Età Media</th>
                          <th>Genere</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topAssociations?.associations?.map((assoc, idx) => (
                          <tr key={assoc.id}>
                            <td>
                              {idx < 3 ? (
                                <Badge bg={idx === 0 ? 'warning' : idx === 1 ? 'secondary' : 'danger'}>{idx + 1}</Badge>
                              ) : (
                                idx + 1
                              )}
                            </td>
                            <td>
                              <div className="fw-bold">{assoc.name}</div>
                              <small className="text-muted">{assoc.associationCode}</small>
                            </td>
                            <td>{assoc.city || '-'}</td>
                            <td>
                              <small>
                                {assoc.territory}
                                <br />
                                <span className="text-muted">{assoc.region}</span>
                              </small>
                            </td>
                            <td>
                              <Badge bg="success">{assoc.memberCount}</Badge>
                            </td>
                            <td>
                              <Badge bg={assoc.newMembers30d > 0 ? 'primary' : 'secondary'}>+{assoc.newMembers30d}</Badge>
                            </td>
                            <td>{assoc.avgAge || '-'}</td>
                            <td>
                              <small>{assoc.genderRatio}</small>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Tab>

              {/* Anno Precedente tab */}
              <Tab eventKey="yearComparison" title={<><i className="bi bi-calendar2-range me-2"></i>Anno Precedente</>}>
                {yearComparison ? (
                  <>
                    {/* Summary cards */}
                    <Row className="mb-4">
                      <Col md={3}>
                        <Card className="h-100 border-0 shadow-sm" style={{ borderLeft: '4px solid #006e39' }}>
                          <Card.Body className="text-center">
                            <h6 className="text-muted mb-1">Tesserati {yearComparison.currentYear} (stesso periodo)</h6>
                            <h3 style={{ color: '#006e39' }}>
                              {yearComparison.summary?.currentYear?.samePeriodRegistrations?.toLocaleString('it-IT') || 0}
                            </h3>
                            {yearComparison.summary?.samePeriodDelta?.registrationsPercent != null && (
                              <Badge bg={parseFloat(yearComparison.summary.samePeriodDelta.registrationsPercent) >= 0 ? 'success' : 'danger'}>
                                {parseFloat(yearComparison.summary.samePeriodDelta.registrationsPercent) >= 0 ? '+' : ''}
                                {yearComparison.summary.samePeriodDelta.registrationsPercent}%
                              </Badge>
                            )}
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col md={3}>
                        <Card className="h-100 border-0 shadow-sm" style={{ borderLeft: '4px solid #9c27b0' }}>
                          <Card.Body className="text-center">
                            <h6 className="text-muted mb-1">Tesserati {yearComparison.previousYear} (stesso periodo)</h6>
                            <h3 style={{ color: '#9c27b0' }}>
                              {yearComparison.summary?.previousYear?.samePeriodRegistrations?.toLocaleString('it-IT') || 0}
                            </h3>
                            <small className="text-muted">Gennaio – {['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'][yearComparison.currentMonth - 1]}</small>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col md={3}>
                        <Card className="h-100 border-0 shadow-sm" style={{ borderLeft: '4px solid #0077b6' }}>
                          <Card.Body className="text-center">
                            <h6 className="text-muted mb-1">Totale anno {yearComparison.previousYear}</h6>
                            <h3 style={{ color: '#0077b6' }}>
                              {yearComparison.summary?.previousYear?.totalRegistrations?.toLocaleString('it-IT') || 0}
                            </h3>
                            <small className="text-muted">Anno completo</small>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col md={3}>
                        <Card className="h-100 border-0 shadow-sm" style={{ borderLeft: '4px solid #e76f51' }}>
                          <Card.Body className="text-center">
                            <h6 className="text-muted mb-1">Associazioni attive {yearComparison.previousYear}</h6>
                            <h3 style={{ color: '#e76f51' }}>
                              {yearComparison.summary?.previousYear?.totalAssociations?.toLocaleString('it-IT') || 0}
                            </h3>
                            {yearComparison.summary?.samePeriodDelta?.associationsPercent != null && (
                              <Badge bg={parseFloat(yearComparison.summary.samePeriodDelta.associationsPercent) >= 0 ? 'success' : 'danger'}>
                                {parseFloat(yearComparison.summary.samePeriodDelta.associationsPercent) >= 0 ? '+' : ''}
                                {yearComparison.summary.samePeriodDelta.associationsPercent}% YoY
                              </Badge>
                            )}
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>

                    {/* Monthly comparison chart */}
                    <Card className="mb-4">
                      <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                        <strong>
                          <i className="bi bi-bar-chart-line me-2"></i>
                          Confronto mensile: {yearComparison.currentYear} vs {yearComparison.previousYear}
                        </strong>
                        <Badge bg="info">I mesi futuri di {yearComparison.currentYear} sono vuoti</Badge>
                      </Card.Header>
                      <Card.Body>
                        <ResponsiveContainer width="100%" height={320}>
                          <BarChart data={yearComparison.monthlyComparison} barCategoryGap="20%">
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="monthName" />
                            <YAxis />
                            <Tooltip
                              formatter={(value, name) => [value.toLocaleString('it-IT'), name]}
                              labelFormatter={label => `Mese: ${label}`}
                            />
                            <Legend />
                            <Bar dataKey="previousYear" fill="#9c27b0" name={String(yearComparison.previousYear)} />
                            <Bar dataKey="currentYear" fill="#006e39" name={String(yearComparison.currentYear)} />
                          </BarChart>
                        </ResponsiveContainer>
                      </Card.Body>
                    </Card>

                    {/* Delta chart */}
                    <Card className="mb-4">
                      <Card.Header className="bg-white">
                        <strong>
                          <i className="bi bi-graph-up-arrow me-2"></i>
                          Variazione mensile {yearComparison.currentYear} vs {yearComparison.previousYear}
                        </strong>
                      </Card.Header>
                      <Card.Body>
                        <ResponsiveContainer width="100%" height={260}>
                          <BarChart data={yearComparison.monthlyComparison.filter(m => !m.isFuture)}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="monthName" />
                            <YAxis />
                            <Tooltip
                              formatter={(value, name) => [
                                `${value >= 0 ? '+' : ''}${value.toLocaleString('it-IT')}`,
                                name
                              ]}
                            />
                            <ReferenceLine y={0} stroke="#666" />
                            <Bar
                              dataKey="delta"
                              name="Variazione tesserati"
                              label={false}
                            >
                              {yearComparison.monthlyComparison
                                .filter(m => !m.isFuture)
                                .map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.delta >= 0 ? '#006e39' : '#e76f51'} />
                                ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                        <small className="text-muted">
                          <i className="bi bi-info-circle me-1"></i>
                          Verde = crescita rispetto allo stesso mese dell'anno scorso &nbsp;|&nbsp; Rosso = calo
                        </small>
                      </Card.Body>
                    </Card>

                    {/* Regional comparison */}
                    {yearComparison.regionalComparison?.length > 0 && (
                      <Row className="mb-4">
                        <Col lg={8}>
                          <Card>
                            <Card.Header className="bg-white">
                              <strong>
                                <i className="bi bi-geo-alt me-2"></i>
                                Confronto regionale {yearComparison.currentYear} vs {yearComparison.previousYear}
                              </strong>
                            </Card.Header>
                            <Card.Body>
                              <ResponsiveContainer width="100%" height={350}>
                                <BarChart data={yearComparison.regionalComparison} layout="vertical" barCategoryGap="20%">
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis type="number" />
                                  <YAxis dataKey="region" type="category" width={140} tick={{ fontSize: 11 }} />
                                  <Tooltip formatter={value => value.toLocaleString('it-IT')} />
                                  <Legend />
                                  <Bar dataKey="previousYear" fill="#9c27b0" name={String(yearComparison.previousYear)} />
                                  <Bar dataKey="currentYear" fill="#006e39" name={String(yearComparison.currentYear)} />
                                </BarChart>
                              </ResponsiveContainer>
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col lg={4}>
                          <Card className="h-100">
                            <Card.Header className="bg-white">
                              <strong>
                                <i className="bi bi-table me-2"></i>Dettaglio regioni
                              </strong>
                            </Card.Header>
                            <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                              <Table size="sm" striped hover>
                                <thead style={{ position: 'sticky', top: 0, background: 'white' }}>
                                  <tr>
                                    <th>Regione</th>
                                    <th>{yearComparison.previousYear}</th>
                                    <th>{yearComparison.currentYear}</th>
                                    <th>Δ%</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {yearComparison.regionalComparison.map((r, idx) => (
                                    <tr key={idx}>
                                      <td><small>{r.region}</small></td>
                                      <td>{r.previousYear.toLocaleString('it-IT')}</td>
                                      <td><Badge bg="success">{r.currentYear.toLocaleString('it-IT')}</Badge></td>
                                      <td>
                                        {r.deltaPercent != null ? (
                                          <Badge bg={parseFloat(r.deltaPercent) >= 0 ? 'success' : 'danger'}>
                                            {parseFloat(r.deltaPercent) >= 0 ? '+' : ''}{r.deltaPercent}%
                                          </Badge>
                                        ) : (
                                          <Badge bg="secondary">N/A</Badge>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                    )}

                    {/* Month-by-month table */}
                    <Card>
                      <Card.Header className="bg-white">
                        <strong>
                          <i className="bi bi-calendar3 me-2"></i>
                          Dettaglio mensile – {yearComparison.previousYear} completo vs {yearComparison.currentYear}
                        </strong>
                      </Card.Header>
                      <Card.Body>
                        <Table striped hover size="sm">
                          <thead>
                            <tr>
                              <th>Mese</th>
                              <th>{yearComparison.previousYear}</th>
                              <th>{yearComparison.currentYear}</th>
                              <th>Differenza</th>
                              <th>Variazione %</th>
                            </tr>
                          </thead>
                          <tbody>
                            {yearComparison.monthlyComparison.map((m, idx) => (
                              <tr key={idx} className={m.isFuture ? 'text-muted' : ''}>
                                <td><strong>{m.monthName}</strong></td>
                                <td>{m.previousYear.toLocaleString('it-IT')}</td>
                                <td>
                                  {m.isFuture ? (
                                    <Badge bg="secondary">—</Badge>
                                  ) : (
                                    <Badge bg="success">{m.currentYear.toLocaleString('it-IT')}</Badge>
                                  )}
                                </td>
                                <td>
                                  {!m.isFuture && (
                                    <span style={{ color: m.delta >= 0 ? '#006e39' : '#e76f51', fontWeight: 'bold' }}>
                                      {m.delta >= 0 ? '+' : ''}{m.delta.toLocaleString('it-IT')}
                                    </span>
                                  )}
                                </td>
                                <td>
                                  {!m.isFuture && m.deltaPercent != null ? (
                                    <Badge bg={parseFloat(m.deltaPercent) >= 0 ? 'success' : 'danger'}>
                                      {parseFloat(m.deltaPercent) >= 0 ? '+' : ''}{m.deltaPercent}%
                                    </Badge>
                                  ) : !m.isFuture ? (
                                    <Badge bg="secondary">N/A</Badge>
                                  ) : null}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="table-success fw-bold">
                              <td>Totale anno</td>
                              <td>{yearComparison.summary?.previousYear?.totalRegistrations?.toLocaleString('it-IT') || 0}</td>
                              <td>{yearComparison.summary?.currentYear?.samePeriodRegistrations?.toLocaleString('it-IT') || 0} <small className="text-muted">(stesso periodo)</small></td>
                              <td>
                                {yearComparison.summary?.samePeriodDelta?.registrations != null && (
                                  <span style={{ color: yearComparison.summary.samePeriodDelta.registrations >= 0 ? '#006e39' : '#e76f51' }}>
                                    {yearComparison.summary.samePeriodDelta.registrations >= 0 ? '+' : ''}
                                    {yearComparison.summary.samePeriodDelta.registrations.toLocaleString('it-IT')}
                                  </span>
                                )}
                              </td>
                              <td>
                                {yearComparison.summary?.samePeriodDelta?.registrationsPercent != null && (
                                  <Badge bg={parseFloat(yearComparison.summary.samePeriodDelta.registrationsPercent) >= 0 ? 'success' : 'danger'}>
                                    {parseFloat(yearComparison.summary.samePeriodDelta.registrationsPercent) >= 0 ? '+' : ''}
                                    {yearComparison.summary.samePeriodDelta.registrationsPercent}%
                                  </Badge>
                                )}
                              </td>
                            </tr>
                          </tfoot>
                        </Table>
                        <small className="text-muted">
                          <i className="bi bi-info-circle me-1"></i>
                          Il totale del {yearComparison?.currentYear} mostra solo i mesi trascorsi (Gen – {['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'][(yearComparison?.currentMonth || 1) - 1]}).
                        </small>
                      </Card.Body>
                    </Card>
                  </>
                ) : (
                  <Alert variant="info">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Caricamento dati anno precedente…
                  </Alert>
                )}
              </Tab>
            </Tabs>

            {/* AI Analysis Section */}
            {aiEnabled && aiAnalysis && (
              <Card className="mt-4">
                <Card.Header className="bg-primary text-white">
                  <i className="bi bi-stars me-2"></i>
                  <strong>Analisi AI</strong>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={12}>
                      <p>{aiAnalysis.summary}</p>
                      {aiAnalysis.keyInsights && (
                        <div className="mb-3">
                          <h6>Insights Chiave:</h6>
                          <ul>
                            {aiAnalysis.keyInsights.map((insight, idx) => (
                              <li key={idx}>{insight}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}

            {/* Info Alert */}
            <Alert variant="info" className="mt-4 d-flex align-items-center">
              <i className="bi bi-info-circle me-3 fs-4"></i>
              <div>
                <strong>Strumento di Pianificazione Eventi SDA</strong>
                <p className="mb-0 small">
                  Questo strumento analizza i dati sui tesserati della disciplina{' '}
                  <strong>{effectiveDiscipline || overview?.discipline || 'assegnata'}</strong> per aiutarti a pianificare eventi efficaci.
                  {aiEnabled && ' Utilizza i suggerimenti AI per analisi più approfondite.'}
                </p>
              </div>
            </Alert>
          </div>
        </div>
      </div>

      {/* AI Suggestions Modal */}
      <Modal show={showAiModal} onHide={() => setShowAiModal(false)} size="lg">
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title>
            <i className="bi bi-stars me-2"></i>Suggerimenti AI per Eventi
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {aiSuggestions ? (
            <>
              {aiSuggestions.generalAdvice && (
                <Alert variant="info">
                  <strong>Consiglio Generale:</strong> {aiSuggestions.generalAdvice}
                </Alert>
              )}
              {aiSuggestions.suggestions?.map((suggestion, idx) => (
                <Card key={idx} className="mb-3">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <strong>{suggestion.title}</strong>
                    <Badge bg={suggestion.confidenceScore > 0.7 ? 'success' : 'warning'}>
                      Confidenza: {(suggestion.confidenceScore * 100).toFixed(0)}%
                    </Badge>
                  </Card.Header>
                  <Card.Body>
                    <p>{suggestion.description}</p>
                    <Row className="small text-muted">
                      {suggestion.location?.region && <Col md={4}>📍 Regione: {suggestion.location.region}</Col>}
                      {suggestion.timing?.suggestedMonth && (
                        <Col md={4}>📅 Mese: {MONTH_NAMES[suggestion.timing.suggestedMonth - 1]}</Col>
                      )}
                      {suggestion.expectedAttendance && <Col md={4}>👥 Partecipanti attesi: {suggestion.expectedAttendance}</Col>}
                    </Row>
                    <hr />
                    <small className="text-muted">
                      <strong>Motivazione:</strong> {suggestion.reasoning}
                    </small>
                  </Card.Body>
                </Card>
              ))}
            </>
          ) : (
            <Spinner animation="border" />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAiModal(false)}>
            Chiudi
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SdaEventPlanning;
